import React from 'react'
import Input from './input'
import ReactMixin from 'react-mixin'
import ImmutableRenderMixin from 'react-immutable-render-mixin'
import Icon from '_/shared/components/icon'
import icons from '_/config/icons.json'
import { List } from 'immutable'
import isFieldHiddenSfr from '_/utils/isFieldHiddenSfr'
import formatNumber from '_/utils/formatNumber'

/**
 * A Table Row that has the ability to edit
 */
@ReactMixin.decorate(ImmutableRenderMixin)
class TRow extends React.Component {
  static propTypes = {
    account: React.PropTypes.object.isRequired,
    editable: React.PropTypes.bool,
    field: React.PropTypes.string.isRequired,
    link: React.PropTypes.func.isRequired,
    errors: React.PropTypes.object,
    fieldMetadata: React.PropTypes.object,
    fieldData: React.PropTypes.object,
    userMap: React.PropTypes.object,
    genUseTM: React.PropTypes.object,
    isPrivate: React.PropTypes.bool.isRequired,
    isLocked: React.PropTypes.bool.isRequired,
    onCertify: React.PropTypes.func.isRequired,
    value: React.PropTypes.any
  }

  renderLabel () {
    const { field, fieldMetadata, editable, isPrivate } = this.props
    const label = isPrivate && field === 'rediRemarks'
      ? 'Private remarks'
      : fieldMetadata.get('label')

    return (
      <td width="175" className={editable && !isPrivate && fieldMetadata.get('lblCls')}>
        {label}
      </td>
    )
  }

  renderUser () {
    const { fieldData, userMap, isPrivate, link, field } = this.props
    const user = fieldData && fieldData.get('userId')
    const value = link(field).value

    return (
      <td width={37}>
        {user && value &&
          <a href={`mailto:${userMap.getIn([user, 'email'])}`}>
            <img
              width={20}
              className="img-circle"
              title={(isPrivate ? 'Last edited by ' : 'Added by ') + userMap.getIn([user, 'username'])}
              src={`/api/v1/users/${user}.jpg?size=40`} />
          </a>
        }
      </td>
    )
  }

  handleCertify (evt) {
    evt.preventDefault()
    this.props.onCertify(this.props.field)
  }

  renderCertBy () {
    const { fieldData, userMap, isPrivate, value, account } = this.props
    const certById = fieldData && fieldData.get('certById')
    const userId = fieldData && fieldData.get('userId')
    const groupId = fieldData && fieldData.get('groupId')

    // Cannot certify private records
    if (isPrivate) return null

    const canCertify =
      !account.get('isAdmin') && // Cannot be an admin
      value != null && // Field must have a value
      groupId && // Field must be associated with a group
      account.getIn(['group', 'id']) !== groupId && // User must not be in the group
      account.get('id') !== userId // User cannot certify themselves from another group

    return (
      <td width={30}>
        {canCertify &&
          <a
            style={{ cursor: !certById && 'pointer'}}
            onClick={!certById && ::this.handleCertify}
            title={certById ? `Certified by ${userMap.getIn([certById, 'username'])}` : 'Certify this field'}
            className={certById ? 'text-warning' : 'text-muted'}>
            <Icon name={icons.certified} />
          </a>
        }
      </td>
    )
  }

  renderText () {
    const { field, link, fieldMetadata, errors, editable } = this.props
    let { value } = this.props
    const isLink = fieldMetadata.get('isLink')

    if (!editable && value && isLink) {
      value = (
        <div>
          {value.map((ln, idx) => (
            <span key={idx}>
              <a href={ln} target="_blank">{ln}</a>&nbsp;
            </span>
          ))}
        </div>
      )
    }

    return (
      <td>
        {editable
          ? <Input
              name={field}
              type={fieldMetadata.get('isMulti') ? 'text-multi' : 'text'}
              placeholder={fieldMetadata.get('placeholder')}
              errors={errors}
              valueLink={link(field)} />
          : List.isList(value) ? value.join(', ') : value
        }
      </td>
    )
  }

  renderDate () {
    const { field, link, fieldMetadata, errors, editable } = this.props

    return (
      <td>
        {editable
          ? <Input
              name={field}
              type="date"
              placeholder={fieldMetadata.get('placeholder')}
              errors={errors}
              valueLink={link(field)} />
          : link(field).value
        }
      </td>
    )
  }

  renderEmail () {
    const { field, link, fieldMetadata, errors, editable } = this.props

    return (
      <td>
        {editable
          ? <Input
              name={field}
              type="email"
              placeholder={fieldMetadata.get('placeholder')}
              errors={errors}
              valueLink={link(field)} />
          : <a href={`mailto:${link(field).value}`}>{link(field).value}</a>
        }
      </td>
    )
  }

  renderTel () {
    const { field, link, fieldMetadata, errors, editable } = this.props

    return (
      <td>
        {editable
          ? <Input
              name={field}
              type="tel"
              placeholder={fieldMetadata.get('placeholder')}
              errors={errors}
              valueLink={link(field)} />
          : <a href={`tel:${link(field).value}`}>{link(field).value}</a>
        }
      </td>
    )
  }

  renderNumber () {
    const { field, link, fieldMetadata, errors, editable } = this.props
    const units = fieldMetadata.get('units')
    const isCurrency = units === '$'

    return (
      <td>
        {editable
          ? <Input
              name={field}
              addonBefore={isCurrency && units}
              addonAfter={!isCurrency && units}
              type="number"
              placeholder={fieldMetadata.get('placeholder')}
              errors={errors}
              valueLink={link(field)} />
          : formatNumber(link(field).value, units)
        }
      </td>
    )
  }

  renderCheckbox () {
    const { field, link, errors, editable } = this.props

    return (
      <td>
        {editable
          ? <Input
              name={field}
              type="switch"
              errors={errors}
              checkedLink={link(field)} />
          : link(field).value ? 'Yes' : 'No'
        }
      </td>
    )
  }

  renderTextarea () {
    const { field, link, fieldMetadata, errors, editable } = this.props

    return (
      <td>
        {editable
          ? <Input
              name={field}
              type="textarea"
              placeholder={fieldMetadata.get('placeholder')}
              errors={errors}
              valueLink={link(field)} />
          : link(field).value ? link(field).value.split('\n').map(v => <p style={{marginBottom:0}}>{v}</p>) : ''
        }
      </td>
    )
  }

  renderSelect () {
    const { account, field, link, fieldMetadata, errors, editable, value } = this.props
    const group = account.get('group')

    let enumValues = fieldMetadata.get('enumValues')
    if (!account.get('isAdmin')) {
      if (field === 'useTM') {
        if (account.get('isAgRes')) {
          if (group.get('isSfr')) {
            enumValues = enumValues.filter(v => {
              return v.get('label').indexOf('Single Family Residential:') !== -1
            })
          }
          if (group.get('isAg')) {
            enumValues = enumValues.filter(v => {
              return v.get('label').indexOf('Agriculture:') !== -1
            })
          }
        }
        else {
          enumValues = enumValues.filter(v => {
            return v.get('label').indexOf('Single Family Residential:') === -1 &&
                   v.get('label').indexOf('Agriculture:') === -1
          })
        }
      }

      if (field === 'genUseTM' && !account.get('isAgRes')) {
        enumValues = enumValues.filter(v => {
          return v !== 'Single Family Residential' &&
                 v !== 'Agriculture'
        })
      }
    }

    return (
      <td>
        {editable && fieldMetadata.has('enumValues')
          ? <Input
              type="select"
              name={field}
              errors={errors}
              multi={!!fieldMetadata.get('isMulti')}
              options={enumValues.map(v => ({
                value: v.get ? v.get('value') : v,
                label: v.get ? v.get('label') : v
              })).toJS()}
              valueLink={link(field)} />
          : List.isList(value) ? value.join(', ') : value
        }
      </td>
    )
  }

  isValueHidden () {
    const { fieldMetadata, account, isLocked, fieldData } = this.props
    const groupType = account.getIn(['group', 'type'])
    const groupId = fieldData && fieldData.get('groupId')
    const userId = fieldData && fieldData.get('userId')

    const dataAvailable =
      groupType === 'Point' && // Only applies to point group
      isLocked && // Record hasn't been unlocked
      !fieldMetadata.get('unlocked') && // Field isn't pre-unlocked
      userId && // Data exists in the field
      account.getIn(['group', 'id']) !== groupId // Wasn't shared by group

    return dataAvailable
  }

  isValueIncluded () {
    const { account, field, fieldMetadata, genUseTM } = this.props

    // Exclude genUseTM for AgRes
    if (account.get('isAgRes') && field === 'genUseTM') return false

    const include = fieldMetadata.get('include')
    const isIncluded =
      !include.size || // Include all
      genUseTM.some(t => include.includes(t)) // Include one of genUseTM types

    return isIncluded
  }

  isFieldHidden () {
    const { account, field } = this.props
    const group = account.get('group')

    // Exculde fields if SFR
    if (group.get('isSfr') && isFieldHiddenSfr(field)) return false

    // Exclude SFR Only fields
    if (!group.get('isSfr') && field === 'leaseFeeT') return false

    // Exclude commodityTM from anything but AG
    if (!account.get('isAdmin') && !group.get('isAg') && field === 'commodityTM') return false

    // Catylist ID only visible for admins.
    if (!account.get('isAdmin') && field === 'catylistId') return false

    // Sale type only visible for admins.
    // if (!account.get('isAdmin') && field === 'saleT') return false

    return true
  }

  render() {
    const { fieldMetadata, genUseTM, account } = this.props
    if (!fieldMetadata) return null
    const groupType = account.getIn(['group', 'type'])
    const hiddenEl = <tr className="hide" />

    // Is field not visible in traction
    if (!this.isFieldHidden()) return hiddenEl

    // Is field included in genUseTM
    if (genUseTM && !this.isValueIncluded()) return hiddenEl

    // Only show limited fields for limited users
    if (groupType === 'Limited' && !fieldMetadata.get('limited')) return hiddenEl

    // Mask locked fields with data
    if (groupType === 'Point' && this.isValueHidden()) {
      return (
        <tr>
          {this.renderLabel()}
          <td><i className="text-muted">Data available...</i></td>
          <td />
          <td />
        </tr>
      )
    }

    const inputType = fieldMetadata.get('inputType')
    const renderers = {
      text: ::this.renderText,
      date: ::this.renderDate,
      email: ::this.renderEmail,
      tel: ::this.renderTel,
      number: ::this.renderNumber,
      checkbox: ::this.renderCheckbox,
      textarea: ::this.renderTextarea,
      select: ::this.renderSelect
    }

    return (
      <tr>
        {this.renderLabel()}
        {renderers[inputType]()}
        {this.renderUser()}
        {this.renderCertBy()}
      </tr>
    )

  }
}

export default TRow



// WEBPACK FOOTER //
// ./modules/shared/components/tRow.js